import { createTheme } from "@mui/material";

// Create a custom theme specifically for admin pages with condensed styling
export const baseDobbyTheme = createTheme({
  palette: {
    primary: {
      main: "#1976d2",
    },
    error: {
      main: "#d32f2f",
      contrastText: "#ffffff",
    },
    grey: {
      50: "#fafafa",
      100: "#f5f5f5",
    },
    action: {
      hover: "rgba(0, 0, 0, 0.04)",
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          minHeight: "40px",
          maxHeight: "40px",
          boxSizing: "border-box",
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          maxHeight: "32px !important",
          minHeight: "32px !important",
          paddingTop: "4px",
          paddingBottom: "4px",
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: "32px",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontSize: "0.75rem",
          padding: "4px 12px",
          minHeight: "28px",
        },
        sizeSmall: {
          fontSize: "0.7rem",
          padding: "2px 8px",
          minHeight: "24px",
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          "& .MuiTable-root": {
            tableLayout: "fixed",
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: "8px 16px",
        },
        head: {
          fontSize: "0.75rem",
          fontWeight: 600,
          backgroundColor: "#fafafa",
          padding: "8px 16px",
        },
        body: {
          fontSize: "0.75rem",
          padding: "4px 16px",
        },
        sizeSmall: {
          padding: "4px 8px",
          "&.MuiTableCell-head": {
            padding: "8px",
          },
          "&.MuiTableCell-body": {
            padding: "4px 8px",
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          fontSize: "0.7rem",
          height: "20px",
        },
        sizeSmall: {
          fontSize: "0.625rem",
          height: "18px",
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          padding: "8px",
        },
        sizeSmall: {
          padding: "4px",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiInputBase-input": {
            fontSize: "0.75rem",
            padding: "8px 8px",
            height: "1.2em",
            "&::placeholder": {
              fontSize: "0.75rem !important",
              opacity: "0.6 !important",
            },
          },
          "& .MuiOutlinedInput-root": {
            minHeight: "32px",
            "& .MuiInputBase-input": {
              padding: "8px 8px",
              height: "1.2em",
              "&::placeholder": {
                fontSize: "0.75rem !important",
                opacity: "0.6 !important",
              },
            },
          },
        },
      },
    },
    // @ts-ignore-next-line
    MuiPickersInputBase: {
      styleOverrides: {
        root: {
          "& .MuiPickersSectionList-root.MuiPickersInputBase-sectionsContainer":
            {
              paddingTop: "6px",
              paddingBottom: "6px",
            },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: "0.75rem !important",

          // only when parent is FormControl
          "&.MuiInputLabel-sizeMedium": {
            // Base position BEFORE shrink
            top: "-9px",
          },

          // Shrunk label (floating)
          "&.MuiInputLabel-shrink": {
            fontSize: "0.75rem",
            top: "3px",
          },

          // Only apply when BOTH: shrunk + sizeMedium
          "&.MuiInputLabel-shrink.MuiInputLabel-sizeMedium": {
            top: "2px",
          },
        },

        sizeSmall: {
          fontSize: "0.75rem !important",
        },

        outlined: {
          fontSize: "0.75rem !important",
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          fontSize: "0.75rem",
        },
        select: {
          fontSize: "0.75rem",
          padding: "6px 8px",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: "0.875rem",
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: "16px",
          "&:last-child": {
            paddingBottom: "16px",
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: 4, // shrink hitbox
          "& .MuiSvgIcon-root": {
            fontSize: 14, // default ~24px
          },
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          fontSize: "0.8rem",
          lineHeight: 1.1,
        },
      },
    },
  },
  typography: {
    fontSize: 12,
    h1: {
      fontSize: "1.75rem",
      fontWeight: 600,
    },
    h2: {
      fontSize: "1.5rem",
      fontWeight: 600,
    },
    h3: {
      fontSize: "1.25rem",
      fontWeight: 600,
    },
    h4: {
      fontSize: "1.125rem", // Significantly reduced from default
      fontWeight: 600,
    },
    h5: {
      fontSize: "1rem",
      fontWeight: 600,
    },
    h6: {
      fontSize: "0.875rem",
      fontWeight: 600,
    },
    body1: {
      fontSize: "0.875rem",
    },
    body2: {
      fontSize: "0.75rem",
    },
    caption: {
      fontSize: "0.625rem",
    },
  },
});
